import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getLastRefresh, useDateSubscription } from '../../../bundles/application';

import AlarmHeader from './AlarmHeader';
import AlarmList from './AlarmList';
import AlarmModal from './AlarmModal/AlarmModal';
import { requestAlarms, getAlarmsByTab, getAlarmsTotalByTab } from '../../../bundles/alarms';
import { getActiveSite } from '../../../bundles/sites';
import {
  getStaticComponents,
  getComponentsUpdatedAt,
  getScopedComponents,
  getFilteredActiveComponentId,
} from '../../../bundles/components';
import { ALARM_TABS, buildAlarmQuery } from './utils';
import { API_BATCH_SIZE } from './constants';

import './AlarmPanel.scss';

const AlarmPanel = ({ panelId, componentScope, alarmColumns, isFullScreen = false }) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const [activeTab, setActiveTab] = useState(ALARM_TABS.NEW);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const searchDebounceRef = useRef(null);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [sortBy, setSortBy] = useState('lastOccurrence');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [alarmQueryParameters, setAlarmQueryParameters] = useState(null);
  const userRefresh = useSelector(getLastRefresh);

  const activeComponentId = useSelector(getFilteredActiveComponentId);
  const components = useSelector(getStaticComponents);
  const componentsUpdatedAt = useSelector(getComponentsUpdatedAt);

  const currentAlarms = useSelector((state) => getAlarmsByTab(state, activeTab));
  const newAlarmsTotalRaw = useSelector((state) => getAlarmsTotalByTab(state, ALARM_TABS.NEW));
  const acknowledgedAlarmsTotalRaw = useSelector((state) =>
    getAlarmsTotalByTab(state, ALARM_TABS.ACKNOWLEDGED)
  );
  const resolvedAlarmsTotalRaw = useSelector((state) =>
    getAlarmsTotalByTab(state, ALARM_TABS.RESOLVED)
  );

  const componentScopeWithDefaultTypeFilter = useMemo(
    () => ({
      ...componentScope,
      activeComponentTypeFilter: componentScope?.activeComponentTypeFilter || 'any',
    }),
    [componentScope]
  );

  const scopedComponents = useMemo(
    () =>
      getScopedComponents({
        components,
        componentsUpdatedAt,
        activeComponentId,
        componentScope: componentScopeWithDefaultTypeFilter,
      }),
    [componentsUpdatedAt, activeComponentId, componentScopeWithDefaultTypeFilter, components]
  );

  const scopedComponentDesignations = useMemo(
    () => scopedComponents.map((c) => c.itemDesignation).filter(Boolean),
    [scopedComponents]
  );

  const pollingDateRange = useDateSubscription(panelId);

  // Determine API filter parameters based on component scope
  const getFilterParameters = useCallback(() => {
    const scope = componentScope?.selectedScope;
    const designations = scopedComponentDesignations || [];

    // When scope is 'all', don't send any component filters
    if (scope === 'all') {
      return {
        deviatingReferenceDesignations: undefined,
        includeChildren: undefined,
        shouldFetch: true,
      };
    }

    // For 'active_*_all' scopes: when no component is actively selected (activeComponentId is null),
    // the "else all" part means show all alarms without filtering
    if (!activeComponentId && (scope === 'active_all' || scope === 'active_children_all')) {
      return {
        deviatingReferenceDesignations: undefined,
        includeChildren: undefined,
        shouldFetch: true,
      };
    }

    // For 'active_*_none' scopes: when no component is selected, don't fetch anything
    if (!activeComponentId && (scope === 'active_none' || scope === 'active_children_none')) {
      return {
        deviatingReferenceDesignations: undefined,
        includeChildren: undefined,
        shouldFetch: false,
      };
    }

    // When a component is actively selected, apply filtering
    const includeChildren = scope === 'active_children_all' || scope === 'active_children_none';
    return {
      deviatingReferenceDesignations: designations,
      includeChildren,
      shouldFetch: true,
    };
  }, [componentScope, scopedComponentDesignations, activeComponentId]);

  const { deviatingReferenceDesignations, includeChildren, shouldFetch } = useMemo(
    () => getFilterParameters(),
    [getFilterParameters]
  );

  // Hide alarms and zero out counts when scope says don't fetch (e.g., active_none with no component)
  const filteredAlarms = shouldFetch ? currentAlarms || [] : [];
  const newAlarmsTotal = shouldFetch ? newAlarmsTotalRaw : 0;
  const acknowledgedAlarmsTotal = shouldFetch ? acknowledgedAlarmsTotalRaw : 0;
  const resolvedAlarmsTotal = shouldFetch ? resolvedAlarmsTotalRaw : 0;

  const from = pollingDateRange?.startDate;
  const to = pollingDateRange?.endDate;

  // Excluded columns per tab — empty array means show all columns
  const excludedColumnsByTab = useMemo(
    () => alarmColumns || {},
    // stringify to detect deep changes when nested objects are updated in store
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(alarmColumns || {})]
  );

  const excludedColumns = useMemo(() => {
    let key = 'new';
    if (activeTab === ALARM_TABS.ACKNOWLEDGED) {
      key = 'acknowledged';
    } else if (activeTab === ALARM_TABS.RESOLVED) {
      key = 'resolved';
    }
    const tabCols = excludedColumnsByTab?.[key];
    return Array.isArray(tabCols) ? tabCols : [];
  }, [excludedColumnsByTab, activeTab]);

  // Fetch all alarm states to get totals for tabs
  const fetchAllAlarms = useCallback(() => {
    if (!site?.id || !site?.org || !shouldFetch) return;

    const query = buildAlarmQuery({
      site,
      state: 'new',
      offset: 0,
      limit: API_BATCH_SIZE,
      from,
      to,
      sortBy,
      sortOrder,
      deviatingReferenceDesignations,
    });

    if (config.onPremise) {
      delete query.site;
    }

    dispatch(requestAlarms({ ...query, state: 'new' }));
    dispatch(requestAlarms({ ...query, state: 'acknowledged' }));
    dispatch(requestAlarms({ ...query, state: 'resolved' }));
    setAlarmQueryParameters(query);
  }, [site, dispatch, from, to, shouldFetch, deviatingReferenceDesignations, sortBy, sortOrder]);

  // Initial fetch and re-fetch on global refresh or date range change
  useEffect(() => {
    fetchAllAlarms();
  }, [fetchAllAlarms, userRefresh]);

  // Handle tab change
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setSearchText(''); // Clear search when switching tabs
    setDebouncedSearchText('');
  }, []);

  // Debounce search text to avoid filtering on every keystroke
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 200);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchText]);

  const handleSearchChange = useCallback((event) => {
    const value = event?.target ? event.target.value : event;
    setSearchText(value);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchAllAlarms();
  }, [fetchAllAlarms]);

  const handleAlarmClick = useCallback((alarm) => {
    setSelectedAlarm(alarm);
    setShowAlarmModal(true);
  }, []);

  const handleSortChange = useCallback(
    (columnId) => {
      // Toggle sort order if clicking the same column, otherwise default to DESC
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'DESC' ? 'ASC' : 'DESC');
      } else {
        setSortBy(columnId);
        setSortOrder('DESC');
      }
    },
    [sortBy, sortOrder]
  );

  return (
    <div className="alarm-panel">
      <AlarmHeader
        panelId={panelId}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        searchText={searchText}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        newAlarmsCount={newAlarmsTotal}
        acknowledgedAlarmsCount={acknowledgedAlarmsTotal}
        resolvedAlarmsCount={resolvedAlarmsTotal}
        from={from}
        to={to}
        sortBy={sortBy}
        sortOrder={sortOrder}
        deviatingReferenceDesignations={deviatingReferenceDesignations}
        includeChildren={includeChildren}
        exportSearchText={debouncedSearchText}
        canExport={shouldFetch}
      />
      <AlarmList
        onAlarmClick={handleAlarmClick}
        alarms={filteredAlarms}
        site={site}
        activeTab={activeTab}
        searchText={debouncedSearchText}
        from={from}
        to={to}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        deviatingReferenceDesignations={deviatingReferenceDesignations}
        includeChildren={includeChildren}
        listColumns={excludedColumns}
        isFullScreen={isFullScreen}
      />
      {showAlarmModal && (
        <AlarmModal
          alarm={selectedAlarm}
          activeTab={activeTab}
          setSelectedAlarm={setSelectedAlarm}
          onClose={() => setShowAlarmModal(false)}
          alarmQueryParameters={alarmQueryParameters}
        />
      )}
    </div>
  );
};

export default AlarmPanel;
